import { Link } from 'react-router-dom';
import { Mail, MessageCircle } from 'lucide-react';
import { motion } from 'framer-motion';

const Footer = () => {
  return (
    <motion.footer 
        className="bg-gradient-to-br from-gray-900 via-gray-900 to-gray-800 text-white" 
        data-testid="footer"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {/* Logo and Tagline */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="relative">
                  <img 
                    src="/logo.jpg" 
                    alt="Prodizzy Logo"
                    className="h-14 w-14 rounded-full object-cover border-2 border-purple-500 shadow-lg shadow-purple-500/50"
                    data-testid="footer-logo"
                  />
                  <div className="absolute inset-0 rounded-full bg-purple-500/20 animate-pulse"></div>
                </div>
                <div>
                  <h3 className="text-xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">Prodizzy</h3>
                  <p className="text-xs text-gray-400">Real World Product Challenge</p>
                </div>
              </div>
              <p className="text-sm text-gray-300 leading-relaxed pl-1">
                Empowering students to collaborate, innovate, and make real impact.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-lg font-bold mb-4 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">Quick Links</h3>
              <ul className="space-y-3">
                <li>
                  <Link to="/" className="text-gray-300 hover:text-purple-400 transition inline-flex items-center group" data-testid="footer-link-home">
                    <span className="w-1.5 h-1.5 bg-purple-500 rounded-full mr-2 group-hover:scale-150 transition-transform"></span>
                    Home
                  </Link>
                </li>
                <li>
                  <Link to="/explore" className="text-gray-300 hover:text-purple-400 transition inline-flex items-center group" data-testid="footer-link-explore">
                    <span className="w-1.5 h-1.5 bg-purple-500 rounded-full mr-2 group-hover:scale-150 transition-transform"></span>
                    Explore Projects
                  </Link>
                </li>
                <li>
                  <Link to="/submit-idea" className="text-gray-300 hover:text-purple-400 transition inline-flex items-center group" data-testid="footer-link-submit">
                    <span className="w-1.5 h-1.5 bg-purple-500 rounded-full mr-2 group-hover:scale-150 transition-transform"></span>
                    Submit an Idea
                  </Link>
                </li>
                <li>
                  <Link to="/community" className="text-gray-300 hover:text-purple-400 transition inline-flex items-center group" data-testid="footer-link-community">
                    <span className="w-1.5 h-1.5 bg-purple-500 rounded-full mr-2 group-hover:scale-150 transition-transform"></span>
                    Join Our Community
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="text-gray-300 hover:text-purple-400 transition inline-flex items-center group" data-testid="footer-link-contact">
                    <span className="w-1.5 h-1.5 bg-purple-500 rounded-full mr-2 group-hover:scale-150 transition-transform"></span>
                    Contact Us
                  </Link>
                </li>
              </ul>
            </div>

            {/* Connect */}
            <div>
              <h3 className="text-lg font-bold mb-4 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">Connect</h3>
              <div className="space-y-4">
                <a 
                  href="mailto:contact@prodizzy.com" 
                  className="flex items-center text-gray-300 hover:text-purple-400 transition group" 
                  data-testid="footer-email"
                >
                  <div className="p-2 bg-purple-600/20 rounded-lg mr-3 group-hover:bg-purple-600/40 transition">
                    <Mail size={20} className="text-purple-400" />
                  </div>
                  <span className="text-sm">contact@prodizzy.com</span>
                </a>
                
                <div className="pt-2">
                  <p className="text-xs text-gray-400 mb-3">Follow us on social media</p>
                  <div className="flex items-center gap-3">
                    <a 
                      href="#" 
                      className="p-2.5 bg-gray-800 rounded-lg hover:bg-purple-600 transition-all duration-300 hover:scale-110 group" 
                      title="WhatsApp" 
                      data-testid="footer-whatsapp"
                    >
                      <MessageCircle size={20} className="text-gray-300 group-hover:text-white" />
                    </a>
                    <a 
                      href="#" 
                      className="p-2.5 bg-gray-800 rounded-lg hover:bg-purple-600 transition-all duration-300 hover:scale-110 group" 
                      title="LinkedIn" 
                      data-testid="footer-linkedin"
                    >
                      <svg className="w-5 h-5 text-gray-300 group-hover:text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                      </svg>
                    </a>
                    <a 
                      href="#" 
                      className="p-2.5 bg-gray-800 rounded-lg hover:bg-purple-600 transition-all duration-300 hover:scale-110 group" 
                      title="Instagram" 
                      data-testid="footer-instagram"
                    >
                      <svg className="w-5 h-5 text-gray-300 group-hover:text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                      </svg>
                    </a>
                    <a 
                      href="#" 
                      className="p-2.5 bg-gray-800 rounded-lg hover:bg-purple-600 transition-all duration-300 hover:scale-110 group" 
                      title="YouTube" 
                      data-testid="footer-youtube"
                    >
                      <svg className="w-5 h-5 text-gray-300 group-hover:text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/>
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Copyright */}
          <div className="border-t border-gray-800 mt-10 pt-6 text-center">
            <p className="text-sm text-gray-400">
              © <span className="text-purple-400 font-semibold">Prodizzy</span> — Built for Builders, by Builders.
            </p>
          </div>
        </div>
      </motion.footer>
  );
};

export default Footer;
